<template>
  <div class="warikan-view">
    <div class="warikan-container">
      <!-- ローディング表示 -->
      <div v-if="isLoading" class="loading-state">
        <div class="spinner"></div>
        <p>読み込み中...</p>
      </div>

      <!-- データがない場合 -->
      <div v-else-if="members.length === 0" class="empty-state">
        <div class="empty-icon">
          <IconSvg name="yen-sign-solid-full" size="xl" />
        </div>
        <h3 class="empty-title">メンバー情報がありません</h3>
        <p class="empty-description">しおりにメンバーを追加してください</p>
      </div>

      <!-- 割り勘サマリー -->
      <div v-else class="warikan-content">
        <!-- 予算と支出サマリー -->
        <div class="stats-panel">
          <div class="stats-panel-header">
            <div class="stats-panel-title">
              <h2 class="section-title">
                <IconSvg name="yen-sign-solid-full" size="md" /> 統計情報
              </h2>
              <p class="stats-panel-summary">
                現在 {{ formatCurrency(totalExpenseAmount) }}
                <span v-if="hasBudget"> / 予算比 {{ budgetUsagePercent }}%</span>
              </p>
            </div>
            <button
              type="button"
              class="stats-toggle-button"
              :aria-expanded="isStatsOpen"
              @click="isStatsOpen = !isStatsOpen"
            >
              {{ isStatsOpen ? "閉じる" : "開く" }}
            </button>
          </div>

          <div v-show="isStatsOpen" class="budget-section">
            <div class="budget-summary-card">
              <div class="budget-summary-header">
                <h3 class="budget-card-title">予算と支出</h3>
                <span
                  class="budget-status"
                  :class="{ over: isOverBudget, unset: !hasBudget }"
                >
                  {{ budgetStatusLabel }}
                </span>
              </div>

              <div class="budget-metrics">
                <div class="budget-metric">
                  <span class="metric-label">現在の金額</span>
                  <strong>{{ formatCurrency(totalExpenseAmount) }}</strong>
                </div>
                <div class="budget-metric">
                  <span class="metric-label">元の予算</span>
                  <strong>{{ hasBudget ? formatCurrency(originalBudget) : "-" }}</strong>
                </div>
                <div class="budget-metric">
                  <span class="metric-label">予算比</span>
                  <strong>{{ hasBudget ? `${budgetUsagePercent}%` : "-" }}</strong>
                </div>
                <div class="budget-metric">
                  <span class="metric-label">{{
                    isOverBudget ? "予算超過" : "残り予算"
                  }}</span>
                  <strong :class="{ danger: isOverBudget }">
                    {{
                      hasBudget
                        ? formatCurrency(Math.abs(remainingBudget))
                        : "-"
                    }}
                  </strong>
                </div>
              </div>

              <div class="budget-progress" aria-hidden="true">
                <div
                  class="budget-progress-fill"
                  :class="{ over: isOverBudget }"
                  :style="{ width: budgetProgressWidth }"
                ></div>
              </div>
            </div>

            <div class="expense-chart-card">
              <div
                class="expense-donut"
                :style="expenseDonutStyle"
                aria-hidden="true"
              >
                <div class="expense-donut-center">
                  <span>合計</span>
                  <strong>{{ formatCurrency(totalExpenseAmount) }}</strong>
                </div>
              </div>

              <div class="expense-breakdown">
                <div
                  v-for="item in expenseBreakdown"
                  :key="item.key"
                  class="expense-breakdown-row"
                >
                  <div class="breakdown-label-row">
                    <span
                      class="breakdown-color"
                      :style="{ backgroundColor: item.color }"
                    ></span>
                    <span class="breakdown-label">{{ item.label }}</span>
                    <strong>{{ formatCurrency(item.amount) }}</strong>
                  </div>
                  <div class="breakdown-bar" aria-hidden="true">
                    <div
                      class="breakdown-bar-fill"
                      :style="{
                        width: `${item.percent}%`,
                        backgroundColor: item.color,
                      }"
                    ></div>
                  </div>
                </div>
                <p v-if="expenseBreakdown.length === 0" class="no-chart-data">
                  支出データがまだありません
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- メンバー別サマリー -->
        <div class="summary-section">
          <h2 class="section-title">
            <IconSvg name="yen-sign-solid-full" size="md" /> メンバー別支出
          </h2>
          <div class="member-cards">
            <div
              v-for="member in memberSummaries"
              :key="member.memberCode"
              class="member-card"
              :data-testid="`warikan-member-${member.memberCode}`"
              :class="{
                'balance-positive': member.balance > 0,
                'balance-negative': member.balance < 0,
                'balance-zero': member.balance === 0,
              }"
            >
              <div class="member-header">
                <div class="member-info">
                  <div class="member-avatar">
                    <img
                      v-if="getMemberProfileImage(member.memberCode)"
                      :src="getMemberProfileImage(member.memberCode)"
                      :alt="member.memberName"
                      class="avatar-img"
                      loading="lazy"
                      decoding="async"
                    />
                    <IconSvg
                      v-else
                      name="user-solid-full"
                      size="sm"
                      class="avatar-placeholder"
                    />
                  </div>
                  <h3 class="member-name" :title="member.memberName">
                    {{ truncateMemberName(member.memberName) }}
                  </h3>
                </div>
                <div
                  class="balance-badge"
                  :class="{
                    positive: member.balance > 0,
                    negative: member.balance < 0,
                  }"
                >
                  {{ member.balance > 0 ? "+" : ""
                  }}{{ formatCurrency(member.balance) }}
                </div>
              </div>
              <div class="member-details">
                <div class="balance-status" :class="member.balanceStatus" data-testid="warikan-balance-status">
                  {{ member.balanceStatus === "receive" ? "受け取る" : member.balanceStatus === "pay" ? "支払う" : "過不足なし" }}
                </div>
                <div class="detail-row">
                  <span class="detail-label">支払った額:</span>
                  <span class="detail-value paid">{{
                    formatCurrency(member.totalPaid)
                  }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">負担すべき額:</span>
                  <span class="detail-value owed">{{
                    formatCurrency(member.totalOwed)
                  }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">支払件数:</span>
                  <span class="detail-value" data-testid="warikan-payment-count">{{ member.paymentCount }}件</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">負担明細:</span>
                  <span class="detail-value" data-testid="warikan-burden-count">{{ member.burdenItemCount }}件</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 精算リスト -->
        <div v-if="calculationIssues.length" class="calculation-warning" role="alert" data-testid="warikan-calculation-warning">
          <h2>精算額を計算できない明細があります</h2>
          <p>内容を確認してから精算してください。</p>
          <ul><li v-for="issue in calculationIssues" :key="`${issue.type}-${issue.lineNon}`">{{ issue.message }}</li></ul>
        </div>
        <div v-if="settlements.length > 0" class="settlements-section">
          <h2 class="section-title">
            <IconSvg name="recycle-solid-full" size="md" /> 精算方法
          </h2>
          <div class="settlement-list">
            <div
              v-for="settlement in settlements"
              :key="settlement.key"
              class="settlement-item"
              :class="{ settled: settlement.settled }"
            >
              <div class="settlement-content">
                <span class="from-member">{{ settlement.fromName }}</span>
                <span class="arrow">→</span>
                <span class="to-member">{{ settlement.toName }}</span>
                <span
                  class="status-badge"
                  :class="settlement.settled ? 'paid' : 'unpaid'"
                >
                  {{ settlement.settled ? "精算済み" : "未支払" }}
                </span>
              </div>
              <div class="settlement-controls">
                <div class="settlement-amount">
                  {{ formatCurrency(settlement.amount) }}
                </div>
                <button
                  v-if="!readOnly"
                  type="button"
                  :class="[
                    'status-toggle',
                    settlement.settled
                      ? 'status-toggle-undo'
                      : 'status-toggle-complete',
                  ]"
                  :disabled="updatingSettlementKey === settlement.key"
                  @click="toggleSettlementStatus(settlement)"
                >
                  {{ settlement.settled ? "未支払に戻す" : "精算済みにする" }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 精算不要の場合 -->
        <div v-if="calculationIssues.length === 0 && settlements.length === 0" class="no-settlements" data-testid="warikan-no-settlements">
          <div class="success-icon">
            <IconSvg name="check-solid-full" size="xl" />
          </div>
          <h3>精算の必要はありません</h3>
          <p>全員の支払いと負担額が均等です</p>
        </div>

        <!-- 費用詳細 -->
        <div class="expenses-section">
          <h2 class="section-title">
            <IconSvg name="list-solid-full" size="md" /> 費用詳細
          </h2>

          <button
            class="mobile-expense-filter-button"
            @click="expenseFilterSidebarOpen = true"
          >
            <IconSvg name="list-solid-full" size="sm" />
            フィルター
            <span v-if="hasExpenseFilters" class="filter-active-dot"></span>
          </button>

          <div
            v-if="expenseFilterSidebarOpen"
            class="expense-filter-overlay"
            @click="expenseFilterSidebarOpen = false"
          ></div>
          <aside
            :class="[
              'expense-filter-sidebar',
              { 'is-open': expenseFilterSidebarOpen },
            ]"
          >
            <div class="expense-filter-sidebar-header">
              <strong>費用の絞り込み</strong>
              <button @click="expenseFilterSidebarOpen = false">×</button>
            </div>
            <div class="expense-filter-sidebar-content">
              <label>
                種類
                <select v-model="expenseTypeFilter" class="filter-select">
                  <option value="all">すべての費用</option>
                  <option value="schedule">スケジュール</option>
                  <option value="warikan">割り勘</option>
                </select>
              </label>
              <label>
                支払者
                <select v-model="expensePayerFilter" class="filter-select">
                  <option value="all">すべての支払者</option>
                  <option
                    v-for="member in members"
                    :key="member.MEMBER_CODE"
                    :value="member.MEMBER_CODE"
                  >
                    {{ member.MEMBER_NAME }}
                  </option>
                </select>
              </label>
              <label>
                負担者
                <select v-model="expenseMemberFilter" class="filter-select">
                  <option value="all">すべての負担者</option>
                  <option
                    v-for="member in members"
                    :key="member.MEMBER_CODE"
                    :value="member.MEMBER_CODE"
                  >
                    {{ member.MEMBER_NAME }}
                  </option>
                </select>
              </label>
              <label>
                精算状態
                <select v-model="expenseStatusFilter" class="filter-select">
                  <option value="all">すべての状態</option>
                  <option value="unsettled">未精算</option>
                  <option value="settled">精算済み</option>
                </select>
              </label>
              <button
                v-if="hasExpenseFilters"
                class="expense-filter-reset"
                @click="resetExpenseFilters"
              >
                リセット
              </button>
            </div>
          </aside>

          <div class="expense-filters desktop-expense-filters">
            <select v-model="expenseTypeFilter" class="filter-select">
              <option value="all">すべての費用</option>
              <option value="schedule">スケジュール</option>
              <option value="warikan">割り勘</option>
            </select>
            <select v-model="expensePayerFilter" class="filter-select">
              <option value="all">すべての支払者</option>
              <option
                v-for="member in members"
                :key="member.MEMBER_CODE"
                :value="member.MEMBER_CODE"
              >
                {{ member.MEMBER_NAME }}
              </option>
            </select>
            <select v-model="expenseMemberFilter" class="filter-select">
              <option value="all">すべての負担者</option>
              <option
                v-for="member in members"
                :key="member.MEMBER_CODE"
                :value="member.MEMBER_CODE"
              >
                {{ member.MEMBER_NAME }}
              </option>
            </select>
            <select v-model="expenseStatusFilter" class="filter-select">
              <option value="all">すべての状態</option>
              <option value="unsettled">未精算</option>
              <option value="settled">精算済み</option>
            </select>
          </div>

          <!-- 統合された費用リスト -->
          <div v-if="filteredExpenses.length === 0" class="no-expenses">
            <p>条件に一致する費用データがありません</p>
          </div>
          <div v-else class="expense-list">
            <!-- スケジュール費用 -->
            <div
              v-for="expense in filteredScheduleExpenses"
              :key="'schedule-' + expense.lineNon"
              class="expense-item"
              :class="{
                'menu-open':
                  openExpenseMenuId === `schedule-${expense.lineNon}`,
              }"
              @click="goToSchedule(expense)"
            >
              <div class="expense-header">
                <div class="expense-title">
                  <span class="expense-icon">
                    <IconSvg
                      :name="getCategoryIcon(expense.category)"
                      size="sm"
                    />
                  </span>
                  <span>{{ expense.title }}</span>
                  <span class="expense-badge schedule">スケジュール</span>
                  <span
                    v-if="expense.isPartialBurden"
                    class="expense-badge partial"
                  >
                    一部メンバー負担
                  </span>
                  <span
                    class="expense-badge"
                    :class="expense.settled ? 'settled' : 'unsettled'"
                  >
                    {{ expense.settled ? "精算済み" : "未精算" }}
                  </span>
                </div>
                <div class="expense-cost">
                  {{ formatExpenseDisplay(expense) }}
                </div>
              </div>
              <div class="mobile-expense-members">
                <div class="mobile-payer-summary">
                  <span class="mobile-summary-label">支払者</span>
                  <span class="payer-chip">
                    <span class="payer-icon" aria-hidden="true">
                      <IconSvg name="yen-sign-solid-full" size="xs" />
                    </span>
                    <span class="payer-name">{{ expense.paidByName }}</span>
                  </span>
                </div>
                <div class="mobile-participant-summary">
                  <span class="mobile-summary-label">参加者</span>
                  <div class="participant-avatars">
                    <span
                      v-for="memberCode in expense.memberCodes.slice(0, 4)"
                      :key="memberCode"
                      class="participant-avatar"
                      :title="getMemberName(memberCode)"
                    >
                      <img
                        v-if="getMemberProfileImage(memberCode)"
                        :src="getMemberProfileImage(memberCode)"
                        :alt="getMemberName(memberCode)"
                        loading="lazy"
                        decoding="async"
                      />
                      <span v-else>{{ getMemberInitial(memberCode) }}</span>
                    </span>
                    <span
                      v-if="expense.memberCodes.length > 4"
                      class="participant-avatar participant-more"
                    >
                      +{{ expense.memberCodes.length - 4 }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="expense-details">
                <div class="expense-info">
                  <span class="info-label">日時:</span>
                  <span>{{ expense.date }} {{ expense.time }}</span>
                </div>
                <div class="expense-info">
                  <span class="info-label">支払者:</span>
                  <span class="payer-chip">
                    <span class="payer-icon" aria-hidden="true">
                      <IconSvg name="yen-sign-solid-full" size="xs" />
                    </span>
                    <span class="payer-name">{{ expense.paidByName }}</span>
                  </span>
                </div>
                <div class="expense-info">
                  <span class="info-label">費用負担者:</span>
                  <span>{{ expense.members.join(", ") }}</span>
                </div>
                <div class="expense-info">
                  <span class="info-label">1人あたり:</span>
                  <span>{{ formatCurrency(expense.perPerson) }}</span>
                </div>
                <div class="expense-info burden-info">
                  <span class="info-label">負担内訳:</span>
                  <div class="burden-breakdown">
                    <span
                      v-for="burden in expense.burdenBreakdown"
                      :key="burden.memberCode"
                      class="burden-chip"
                    >
                      {{ burden.memberName }}:
                      {{ formatCurrency(burden.amount) }}
                    </span>
                  </div>
                </div>
              </div>
              <button
                v-if="!readOnly"
                :class="[
                  'expense-settlement-button',
                  expense.settled ? 'settlement-undo' : 'settlement-complete',
                ]"
                :disabled="updatingExpenseKey === `schedule-${expense.lineNon}`"
                @click.stop="toggleExpenseSettlement(expense)"
              >
                {{ expense.settled ? "未精算に戻す" : "精算済みにする" }}
              </button>
              <div class="expense-actions mobile-expense-actions" @click.stop>
                <button
                  class="expense-kebab-button"
                  @click="toggleExpenseMenu(`schedule-${expense.lineNon}`)"
                  aria-label="費用詳細"
                >
                  ⋮
                </button>
                <div
                  v-if="
                    openExpenseMenuId === `schedule-${expense.lineNon}`
                  "
                  class="expense-action-menu expense-detail-menu"
                >
                  <div class="expense-menu-detail">
                    <span>日時</span>
                    <strong>{{ expense.date }} {{ expense.time }}</strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>支払者</span>
                    <strong class="payer-chip payer-chip-menu">
                      <span class="payer-icon" aria-hidden="true">
                        <IconSvg name="yen-sign-solid-full" size="xs" />
                      </span>
                      <span>{{ expense.paidByName }}</span>
                    </strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>費用負担者</span>
                    <strong>{{ expense.members.join(", ") }}</strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>1人あたり</span>
                    <strong>{{ formatCurrency(expense.perPerson) }}</strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>負担内訳</span>
                    <strong>{{ formatBurdenBreakdown(expense) }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <!-- 独立した割り勘 -->
            <div
              v-for="expense in filteredIndependentWarikans"
              :key="'warikan-' + expense.lineNon"
              class="expense-item warikan-item"
              :class="{
                'menu-open':
                  openExpenseMenuId === `warikan-${expense.lineNon}`,
              }"
            >
              <div class="expense-header">
                <div class="expense-title">
                  <span class="expense-icon">
                    <IconSvg name="bag-shopping-solid-full" size="sm" />
                  </span>
                  <span>{{ expense.title }}</span>
                  <span class="expense-badge independent">割り勘</span>
                  <span
                    class="expense-badge"
                    :class="expense.settled ? 'settled' : 'unsettled'"
                  >
                    {{ expense.settled ? "精算済み" : "未精算" }}
                  </span>
                </div>
                <div class="expense-cost">
                  {{ formatExpenseDisplay(expense) }}
                </div>
              </div>
              <p v-if="expense.memo" class="expense-memo">
                {{ expense.memo }}
              </p>
              <div class="mobile-expense-members">
                <div class="mobile-payer-summary">
                  <span class="mobile-summary-label">支払者</span>
                  <span class="payer-chip">
                    <span class="payer-icon" aria-hidden="true">
                      <IconSvg name="yen-sign-solid-full" size="xs" />
                    </span>
                    <span class="payer-name">{{ expense.paidByName }}</span>
                  </span>
                </div>
                <div class="mobile-participant-summary">
                  <span class="mobile-summary-label">参加者</span>
                  <div class="participant-avatars">
                    <span
                      v-for="memberCode in expense.memberCodes.slice(0, 4)"
                      :key="memberCode"
                      class="participant-avatar"
                      :title="getMemberName(memberCode)"
                    >
                      <img
                        v-if="getMemberProfileImage(memberCode)"
                        :src="getMemberProfileImage(memberCode)"
                        :alt="getMemberName(memberCode)"
                        loading="lazy"
                        decoding="async"
                      />
                      <span v-else>{{ getMemberInitial(memberCode) }}</span>
                    </span>
                    <span
                      v-if="expense.memberCodes.length > 4"
                      class="participant-avatar participant-more"
                    >
                      +{{ expense.memberCodes.length - 4 }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="expense-details">
                <div class="expense-info">
                  <span class="info-label">通貨:</span>
                  <span>{{ expense.currency }}</span>
                </div>
                <div v-if="expense.memo" class="expense-info memo-info">
                  <span class="info-label">メモ:</span>
                  <span>{{ expense.memo }}</span>
                </div>
                <div class="expense-info">
                  <span class="info-label">支払者:</span>
                  <span class="payer-chip">
                    <span class="payer-icon" aria-hidden="true">
                      <IconSvg name="yen-sign-solid-full" size="xs" />
                    </span>
                    <span class="payer-name">{{ expense.paidByName }}</span>
                  </span>
                </div>
                <div class="expense-info">
                  <span class="info-label">参加者:</span>
                  <span>{{ expense.members.join(", ") }}</span>
                </div>
                <div class="expense-info">
                  <span class="info-label">1人あたり:</span>
                  <span>{{ formatCurrency(expense.perPerson) }}</span>
                </div>
                <div class="expense-info burden-info">
                  <span class="info-label">負担内訳:</span>
                  <div class="burden-breakdown">
                    <span
                      v-for="burden in expense.burdenBreakdown"
                      :key="burden.memberCode"
                      class="burden-chip"
                    >
                      {{ burden.memberName }}:
                      {{ formatCurrency(burden.amount) }}
                    </span>
                  </div>
                </div>
              </div>
              <div
                class="expense-actions"
                v-if="!readOnly"
                @click.stop
              >
                <button
                  class="expense-kebab-button"
                  @click="toggleExpenseMenu(`warikan-${expense.lineNon}`)"
                  aria-label="費用メニュー"
                >
                  ⋮
                </button>
                <div
                  v-if="
                    openExpenseMenuId === `warikan-${expense.lineNon}`
                  "
                  class="expense-action-menu expense-detail-menu"
                >
                  <div class="expense-menu-detail">
                    <span>通貨</span>
                    <strong>{{ expense.currency }}</strong>
                  </div>
                  <div v-if="expense.memo" class="expense-menu-detail">
                    <span>メモ</span>
                    <strong>{{ expense.memo }}</strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>支払者</span>
                    <strong class="payer-chip payer-chip-menu">
                      <span class="payer-icon" aria-hidden="true">
                        <IconSvg name="yen-sign-solid-full" size="xs" />
                      </span>
                      <span>{{ expense.paidByName }}</span>
                    </strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>参加者</span>
                    <strong>{{ expense.members.join(", ") }}</strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>1人あたり</span>
                    <strong>{{ formatCurrency(expense.perPerson) }}</strong>
                  </div>
                  <div class="expense-menu-detail">
                    <span>負担内訳</span>
                    <strong>{{ formatBurdenBreakdown(expense) }}</strong>
                  </div>
                  <button
                    class="expense-action-item"
                    @click="runExpenseAction(editWarikan, expense)"
                  >
                    編集
                  </button>
                  <button
                    class="expense-action-item expense-action-delete"
                    @click="
                      runExpenseAction(deleteWarikan, expense.lineNon)
                    "
                  >
                    削除
                  </button>
                </div>
              </div>
              <button
                v-if="!readOnly"
                :class="[
                  'expense-settlement-button',
                  expense.settled ? 'settlement-undo' : 'settlement-complete',
                ]"
                :disabled="updatingExpenseKey === `warikan-${expense.lineNon}`"
                @click.stop="toggleExpenseSettlement(expense)"
              >
                {{ expense.settled ? "未精算に戻す" : "精算済みにする" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- フローティングボタン（割り勘作成） -->
    <button
      v-if="!readOnly"
      @click="openCreateModal"
      class="floating-btn"
      title="割り勘追加"
    >
      <span class="floating-btn-icon">
        <IconSvg name="plus-solid-full" size="md" />
      </span>
    </button>

    <!-- 割り勘作成モーダル -->
    <CreateWarikanModal
      v-if="!readOnly"
      v-model="showModal"
      :shioriId="shioriId"
      :editMode="editMode"
      :warikanData="editingWarikan"
      @create="onWarikanCreated"
      @update="onWarikanUpdated"
    />
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import IconSvg from "@/components/IconSvg.vue";
import CreateWarikanModal from "@/components/CreateWarikanModal.vue";
import { useScheduleStore } from "@/store/schedule-store";
import { useScheduleCategoryStore } from "@/store/schedule-category-store";
import { useShioriMemberStore } from "@/store/shiori-member-store";
import { useShioriStore } from "@/store/shiori-store";
import { useWarikanStore } from "@/store/warikan-store";
import {
  buildExchangeRateMap,
  formatCurrencyAmount,
  formatExpenseAmount,
  formatJpy,
  normalizeCurrency,
  toJpy,
} from "@/utils/exchange-rate";
import { calculateWarikan } from "@/utils/warikan-calculation";

export default {
  name: "WarikanView",
  components: {
    IconSvg,
    CreateWarikanModal,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:activeTab"],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const scheduleStore = useScheduleStore();
    const scheduleCategoryStore = useScheduleCategoryStore();
    const shioriMemberStore = useShioriMemberStore();
    const shioriStore = useShioriStore();
    const warikanStore = useWarikanStore();

    const shioriId = computed(() => route.params.id);
    const isLoading = ref(true);
    const showModal = ref(false);
    const editMode = ref(false);
    const editingWarikan = ref(null);
    const expenseTypeFilter = ref("all");
    const expensePayerFilter = ref("all");
    const expenseMemberFilter = ref("all");
    const expenseStatusFilter = ref("all");
    const updatingSettlementKey = ref("");
    const updatingExpenseKey = ref("");
    const expenseFilterSidebarOpen = ref(false);
    const openExpenseMenuId = ref(null);
    const isStatsOpen = ref(window.innerWidth > 768);

    // メンバー一覧（storeから正しく取得）
    const members = computed(() => {
      const membersData = shioriMemberStore.members[shioriId.value];
      return Array.isArray(membersData) ? membersData : [];
    });

    const exchangeRateMap = computed(() =>
      buildExchangeRateMap(warikanStore.exchangeRates),
    );

    const memberCodeAliases = (memberCode) => {
      const member = members.value.find(
        (item) =>
          item.MEMBER_CODE === memberCode || item.APP_USER_CODE === memberCode,
      );
      return [
        memberCode,
        member?.MEMBER_CODE,
        member?.APP_USER_CODE,
      ].filter(Boolean);
    };

    const isSameMemberCode = (leftCode, rightCode) => {
      if (!leftCode || !rightCode) return false;
      const leftAliases = memberCodeAliases(leftCode);
      const rightAliases = memberCodeAliases(rightCode);
      return leftAliases.some((code) => rightAliases.includes(code));
    };

    const buildBurdenBreakdown = (cost, memberCodes) => {
      if (!memberCodes.length) return [];
      const amount = (Number(cost) || 0) / memberCodes.length;

      return memberCodes.map((memberCode) => {
        const member = members.value.find(
          (item) => item.MEMBER_CODE === memberCode,
        );
        return {
          memberCode,
          memberName: member?.MEMBER_NAME || "不明",
          amount,
        };
      });
    };

    // スケジュール一覧（費用があるもののみ）
    const scheduleExpenses = computed(() => {
      const schedules = scheduleStore.schedules || [];
      return schedules
        .filter((s) => s.COST && Number(s.COST) > 0)
        .map((s) => {
          const memberNames =
            s.MEMBERS?.filter((m) => m.SPLIT_PARTICIPATE_FLG === "1").map(
              (m) => m.MEMBER_NAME,
            ) || [];
          const paidByMember = s.MEMBERS?.find(
            (m) => m.PAID_AMOUNT && Number(m.PAID_AMOUNT) > 0,
          );
          const participantCount = memberNames.length || 1;
          const memberCodes =
            s.MEMBERS?.filter(
              (m) => m.SPLIT_PARTICIPATE_FLG === "1",
            ).map((m) => m.MEMBER_CODE) || [];
          const originalCurrency = normalizeCurrency(s.CURRENCY);
          const converted = toJpy(
            Number(s.COST),
            originalCurrency,
            exchangeRateMap.value,
          );
          const costJpy = converted.amount;

          return {
            lineNon: s.LINE_NON,
            title: s.SUBJECT_NMV,
            category: s.CATEGORY_CDV,
            date: s.START_YMD,
            time: s.START_TIME || "",
            cost: costJpy,
            originalCost: Number(s.COST),
            originalCurrency,
            currency: originalCurrency,
            costJpy,
            missingRate: converted.missingRate,
            paidByName: paidByMember?.MEMBER_NAME || "不明",
            paidByCode: paidByMember?.MEMBER_CODE || null,
            members: memberNames,
            memberCodes,
            isPartialBurden:
              memberCodes.length > 0 && memberCodes.length < members.value.length,
            perPerson: costJpy / participantCount,
            burdenBreakdown: buildBurdenBreakdown(
              costJpy,
              memberCodes,
            ),
            settled: s.EXPENSE_SETTLED_FLG === "1",
            source: s,
            type: "schedule",
          };
        });
    });

    // 独立した割り勘データ
    const independentWarikans = computed(() => {
      const warikans = warikanStore.warikans || [];
      const warikanMembers = warikanStore.warikanMembers || [];

      return warikans
        .filter((w) => !w.SCHEDULE_LINE_NON) // スケジュールに紐づかないもの
        .map((w) => {
          // この割り勘の参加者を取得
          const participantCodes = warikanMembers
            .filter((wm) => wm.LINE_NON === w.LINE_NON)
            .map((wm) => wm.BENEFICIARY_USER_CODE);

          const memberNames = participantCodes
            .map((code) => {
              const member = members.value.find((m) => m.MEMBER_CODE === code);
              return member?.MEMBER_NAME || "";
            })
            .filter((name) => name);

          // 支払者名を取得
          const payer = members.value.find((m) =>
            isSameMemberCode(m.MEMBER_CODE, w.PAYER_USER_CODE),
          );
          const paidByName = payer?.MEMBER_NAME || "不明";

          const participantCount = memberNames.length || 1;
          const originalCurrency = normalizeCurrency(w.CURRENCY);
          const converted = toJpy(
            Number(w.AMOUNT),
            originalCurrency,
            exchangeRateMap.value,
          );
          const costJpy = converted.amount;

          return {
            lineNon: w.LINE_NON,
            title: w.SUBJECT,
            cost: costJpy,
            originalCost: Number(w.AMOUNT),
            originalCurrency,
            currency: originalCurrency,
            costJpy,
            missingRate: converted.missingRate,
            memo: w.MEMO_TEXT || "",
            paidByName: paidByName,
            paidByCode: w.PAYER_USER_CODE,
            members: memberNames,
            memberCodes: participantCodes,
            perPerson: costJpy / participantCount,
            burdenBreakdown: buildBurdenBreakdown(
              costJpy,
              participantCodes,
            ),
            settled: w.EXPENSE_SETTLED_FLG === "1",
            rawData: w,
            type: "warikan",
          };
        });
    });

    // すべての費用（スケジュール + 独立した割り勘）
    const allExpenses = computed(() => {
      return [...scheduleExpenses.value, ...independentWarikans.value];
    });

    const currentShiori = computed(() => {
      return (
        shioriStore.shiori.find((item) => item.SHIORI_NOV === shioriId.value) ||
        shioriStore.sharedShiori.find(
          (item) => item.SHIORI_NOV === shioriId.value,
        )
      );
    });

    const originalBudget = computed(() =>
      Math.round(Number(currentShiori.value?.BUDGET || 0)),
    );

    const hasBudget = computed(() => originalBudget.value > 0);

    const totalExpenseAmount = computed(() =>
      allExpenses.value.reduce(
        (sum, expense) => sum + (Number(expense.cost) || 0),
        0,
      ),
    );

    const budgetUsagePercent = computed(() => {
      if (!hasBudget.value) return 0;
      return Math.round((totalExpenseAmount.value / originalBudget.value) * 100);
    });

    const budgetProgressWidth = computed(() =>
      hasBudget.value ? `${Math.min(budgetUsagePercent.value, 100)}%` : "0%",
    );

    const remainingBudget = computed(
      () => originalBudget.value - totalExpenseAmount.value,
    );

    const isOverBudget = computed(
      () => hasBudget.value && remainingBudget.value < 0,
    );

    const budgetStatusLabel = computed(() => {
      if (!hasBudget.value) return "予算未設定";
      return isOverBudget.value ? "予算超過" : "予算内";
    });

    const expenseChartColors = [
      "#22c55e",
      "#4ade80",
      "#86efac",
      "#bbf7d0",
      "#84cc16",
      "#a3e635",
      "#bef264",
      "#d9f99d",
    ];

    const categoryLabelFallback = {
      restaurant: "食事",
      hotel: "宿泊",
      transport: "交通",
      movement: "移動",
      activity: "観光・体験",
      shopping: "買い物",
      other: "その他",
      warikan: "割り勘",
    };

    const getExpenseCategoryKey = (expense) => {
      if (expense.type === "warikan") return "warikan";
      return expense.category || "other";
    };

    const getExpenseCategoryLabel = (key) => {
      return (
        scheduleCategoryStore.cdvToName[key] ||
        categoryLabelFallback[key] ||
        "その他"
      );
    };

    const expenseBreakdown = computed(() => {
      const total = totalExpenseAmount.value;
      const grouped = allExpenses.value.reduce((acc, expense) => {
        const key = getExpenseCategoryKey(expense);
        acc[key] = (acc[key] || 0) + (Number(expense.cost) || 0);
        return acc;
      }, {});

      return Object.entries(grouped)
        .map(([key, amount], index) => ({
          key,
          label: getExpenseCategoryLabel(key),
          amount,
          percent: total ? Math.round((amount / total) * 100) : 0,
          color: expenseChartColors[index % expenseChartColors.length],
        }))
        .filter((item) => item.amount > 0)
        .sort((a, b) => b.amount - a.amount);
    });

    const expenseDonutStyle = computed(() => {
      if (expenseBreakdown.value.length === 0) {
        return { background: "#e5e7eb" };
      }

      let current = 0;
      const segments = expenseBreakdown.value.map((item) => {
        const start = current;
        current +=
          totalExpenseAmount.value > 0
            ? (item.amount / totalExpenseAmount.value) * 360
            : 0;
        return `${item.color} ${start}deg ${current}deg`;
      });

      return { background: `conic-gradient(${segments.join(", ")})` };
    });

    const filteredExpenses = computed(() => {
      return allExpenses.value.filter((expense) => {
        const typeMatches =
          expenseTypeFilter.value === "all" ||
          expense.type === expenseTypeFilter.value;
        const payerMatches =
          expensePayerFilter.value === "all" ||
          isSameMemberCode(expense.paidByCode, expensePayerFilter.value);
        const memberMatches =
          expenseMemberFilter.value === "all" ||
          expense.memberCodes.some((memberCode) =>
            isSameMemberCode(memberCode, expenseMemberFilter.value),
          );
        const statusMatches =
          expenseStatusFilter.value === "all" ||
          (expenseStatusFilter.value === "settled" && expense.settled) ||
          (expenseStatusFilter.value === "unsettled" && !expense.settled);
        return typeMatches && payerMatches && memberMatches && statusMatches;
      });
    });

    const filteredScheduleExpenses = computed(() =>
      filteredExpenses.value.filter((expense) => expense.type === "schedule"),
    );

    const filteredIndependentWarikans = computed(() =>
      filteredExpenses.value.filter((expense) => expense.type === "warikan"),
    );

    const hasExpenseFilters = computed(
      () =>
        expenseTypeFilter.value !== "all" ||
        expensePayerFilter.value !== "all" ||
        expenseMemberFilter.value !== "all" ||
        expenseStatusFilter.value !== "all",
    );

    const resetExpenseFilters = () => {
      expenseTypeFilter.value = "all";
      expensePayerFilter.value = "all";
      expenseMemberFilter.value = "all";
      expenseStatusFilter.value = "all";
    };

    const warikanCalculation = computed(() =>
      calculateWarikan({
        members: members.value,
        expenses: allExpenses.value,
        settlementStatuses: warikanStore.settlementStatuses,
      }),
    );
    const memberSummaries = computed(() => warikanCalculation.value.memberSummaries);
    const settlements = computed(() => warikanCalculation.value.settlements);
    const calculationIssues = computed(() => warikanCalculation.value.issues);

    const toggleSettlementStatus = async (settlement) => {
      if (props.readOnly) return;
      updatingSettlementKey.value = settlement.key;
      try {
        await warikanStore.updateSettlementStatus(
          shioriId.value,
          settlement,
          !settlement.settled,
        );
      } catch (error) {
        console.error("精算ステータス更新エラー:", error);
        alert("精算ステータスの更新に失敗しました");
      } finally {
        updatingSettlementKey.value = "";
      }
    };

    const toggleExpenseSettlement = async (expense) => {
      if (props.readOnly) return;
      if (!expense.settled) {
        const confirmed = confirm(
          `${expense.title}を精算済みにしますか？\n\n負担内訳\n${formatBurdenBreakdown(
            expense,
            "\n",
          )}`,
        );
        if (!confirmed) return;
      }

      updatingExpenseKey.value = `${expense.type}-${expense.lineNon}`;
      try {
        await warikanStore.updateExpenseSettlementStatus(
          shioriId.value,
          expense,
          !expense.settled,
        );
      } catch (error) {
        console.error("費用明細の精算状態更新エラー:", error);
        alert("費用明細の精算状態を更新できませんでした");
      } finally {
        updatingExpenseKey.value = "";
      }
    };

    // 通貨フォーマット
    const formatCurrency = (amount) => {
      return formatJpy(amount);
    };

    const formatExpenseDisplay = (expense) => {
      if (!expense) return formatJpy(0);
      if (expense.missingRate) {
        return `${formatCurrencyAmount(
          expense.originalCost,
          expense.originalCurrency,
        )} / JPY -`;
      }
      return formatExpenseAmount(
        expense.originalCost,
        expense.originalCurrency,
        exchangeRateMap.value,
      );
    };

    const formatBurdenBreakdown = (expense, separator = "、") => {
      return expense.burdenBreakdown
        .map(
          (burden) =>
            `${burden.memberName}: ${formatCurrency(burden.amount)}`,
        )
        .join(separator);
    };

    const truncateMemberName = (name) => {
      const characters = Array.from(name || "");
      return characters.length > 5
        ? `${characters.slice(0, 5).join("")}...`
        : name;
    };

    // メンバーのプロフィール画像を取得
    const getMemberProfileImage = (memberCode) => {
      const member = members.value.find((m) => m.MEMBER_CODE === memberCode);
      return member?.PROFILE_IMAGE_BASE64 || null;
    };

    const getMemberName = (memberCode) => {
      const member = members.value.find((m) => m.MEMBER_CODE === memberCode);
      return member?.MEMBER_NAME || "不明";
    };

    const getMemberInitial = (memberCode) => {
      return Array.from(getMemberName(memberCode))[0] || "?";
    };

    // カテゴリアイコン
    const getCategoryIcon = (category) => {
      const icons = {
        restaurant: "utensils-solid-full",
        hotel: "bed-solid-full",
        transport: "car-solid",
        activity: "clapperboard-solid-full",
        shopping: "bag-shopping-solid-full",
        other: "map-solid-full",
      };
      return icons[category] || "map-solid-full";
    };

    // 割り勘作成モードで開く
    const openCreateModal = () => {
      if (props.readOnly) return;
      editMode.value = false;
      editingWarikan.value = null;
      showModal.value = true;
    };

    // 割り勘編集
    const editWarikan = (warikan) => {
      if (props.readOnly) return;
      editMode.value = true;
      editingWarikan.value = {
        ...warikan.rawData,
        MEMBERS: warikan.memberCodes,
      };
      showModal.value = true;
    };

    // 割り勘削除
    const deleteWarikan = async (lineNon) => {
      if (props.readOnly) return;
      if (!confirm("この割り勘を削除しますか？")) return;

      try {
        await warikanStore.deleteWarikan(shioriId.value, lineNon);
        alert("割り勘を削除しました");
      } catch (err) {
        console.error("割り勘削除エラー:", err);
        alert("割り勘の削除に失敗しました");
      }
    };

    const toggleExpenseMenu = (lineNon) => {
      if (props.readOnly) return;
      openExpenseMenuId.value =
        openExpenseMenuId.value === lineNon ? null : lineNon;
    };

    const runExpenseAction = (action, value) => {
      if (props.readOnly) return;
      openExpenseMenuId.value = null;
      action(value);
    };

    const closeExpenseMenu = () => {
      openExpenseMenuId.value = null;
    };

    // 割り勘作成完了時の処理
    const onWarikanCreated = async (result) => {
      console.log("割り勘が作成されました:", result);
    };

    // 割り勘更新完了時の処理
    const onWarikanUpdated = async (result) => {
      console.log("割り勘が更新されました:", result);
    };

    // データ読み込み
    onMounted(async () => {
      document.addEventListener("click", closeExpenseMenu);
      try {
        isLoading.value = true;
        const fetches = [
          warikanStore.fetchWarikans(shioriId.value),
          warikanStore.fetchWarikanMembers(shioriId.value),
          warikanStore.fetchSettlementStatuses(shioriId.value),
          warikanStore.fetchExchangeRates(shioriId.value),
        ];
        // すでにデータがある場合はスキップして重複リクエストを防止
        if (scheduleStore.schedules.length === 0) {
          fetches.push(scheduleStore.fetchSchedules(shioriId.value));
        }
        if (!shioriMemberStore.members[shioriId.value]?.length) {
          fetches.push(shioriMemberStore.fetchMembers(shioriId.value));
        }
        if (!currentShiori.value) {
          fetches.push(shioriStore.fetchShiori());
          fetches.push(shioriStore.fetchSharedWithMe());
        }
        if (!scheduleCategoryStore.loaded) {
          fetches.push(scheduleCategoryStore.fetchCategories());
        }
        await Promise.all(fetches);
      } catch (error) {
        console.error("データ読み込みエラー:", error);
      } finally {
        isLoading.value = false;
      }
    });

    onUnmounted(() => {
      document.removeEventListener("click", closeExpenseMenu);
    });

    // スケジュール詳細に遷移
    const goToSchedule = async (expense) => {
      await router.replace({
        query: {
          ...route.query,
          tab: "schedule",
          schedule: expense.lineNon,
        },
      });
      emit("update:activeTab", "schedule");
    };

    return {
      isLoading,
      members,
      memberSummaries,
      settlements,
      calculationIssues,
      scheduleExpenses,
      independentWarikans,
      allExpenses,
      currentShiori,
      originalBudget,
      hasBudget,
      totalExpenseAmount,
      budgetUsagePercent,
      budgetProgressWidth,
      remainingBudget,
      isOverBudget,
      budgetStatusLabel,
      expenseBreakdown,
      expenseDonutStyle,
      filteredExpenses,
      filteredScheduleExpenses,
      filteredIndependentWarikans,
      expenseTypeFilter,
      expensePayerFilter,
      expenseMemberFilter,
      expenseStatusFilter,
      expenseFilterSidebarOpen,
      openExpenseMenuId,
      isStatsOpen,
      hasExpenseFilters,
      updatingSettlementKey,
      updatingExpenseKey,
      showModal,
      editMode,
      editingWarikan,
      shioriId,
      formatCurrency,
      formatExpenseDisplay,
      formatBurdenBreakdown,
      truncateMemberName,
      getMemberProfileImage,
      getMemberName,
      getMemberInitial,
      getCategoryIcon,
      goToSchedule,
      openCreateModal,
      editWarikan,
      deleteWarikan,
      resetExpenseFilters,
      toggleExpenseMenu,
      runExpenseAction,
      toggleSettlementStatus,
      toggleExpenseSettlement,
      onWarikanCreated,
      onWarikanUpdated,
    };
  },
};
</script>

<style scoped>
.warikan-view {
  min-height: calc(100vh - 120px);
  padding: 1rem 0.5rem; /* mobile */
}

.warikan-container {
  max-width: 1200px;
  margin: 0 auto;
}

/* ローディング・空状態 */
.loading-state,
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--color-text-muted);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--color-border);
  border-top-color: var(--color-primary-ink);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: var(--color-text-muted);
}

.empty-title {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-text);
}

.empty-description {
  font-size: 1rem;
  color: var(--color-text-muted);
  opacity: 0.9;
}

/* コンテンツ */
.warikan-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.section-title {
  color: var(--color-text);
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* 予算と支出 */
.stats-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.stats-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-primary-muted);
}

.stats-panel-title {
  min-width: 0;
}

.stats-panel-title .section-title {
  margin-bottom: 0.25rem;
}

.stats-panel-summary {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.stats-toggle-button {
  flex: 0 0 auto;
  padding: 0.5rem 0.85rem;
  border: 1px solid var(--color-primary);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-primary-ink);
  font-weight: 700;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.stats-toggle-button:hover {
  background: var(--color-primary-light);
}

.budget-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.budget-summary-card,
.expense-chart-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1rem;
  box-shadow: var(--shadow-sm);
}

.budget-summary-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.budget-summary-header .section-title {
  margin-bottom: 0;
}

.budget-card-title {
  margin: 0;
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 700;
}

.budget-status {
  padding: 0.25rem 0.65rem;
  border-radius: var(--radius-full);
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
  font-size: 0.75rem;
  font-weight: 700;
}

.budget-status.over {
  background: var(--color-danger-light);
  color: var(--color-danger);
}

.budget-status.unset {
  background: var(--color-background);
  color: var(--color-text-muted);
}

.budget-metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.budget-metric {
  min-width: 0;
  padding: 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
}

.metric-label {
  display: block;
  margin-bottom: 0.25rem;
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.budget-metric strong {
  display: block;
  color: var(--color-text);
  font-size: 1.125rem;
  overflow-wrap: anywhere;
}

.budget-metric strong.danger {
  color: var(--color-danger);
}

.budget-progress {
  height: 0.75rem;
  margin-top: 1rem;
  overflow: hidden;
  border-radius: var(--radius-full);
  background: var(--color-border);
}

.budget-progress-fill {
  height: 100%;
  border-radius: inherit;
  background: var(--color-primary-ink);
  transition: width 0.25s ease;
}

.budget-progress-fill.over {
  background: var(--color-danger);
}

.expense-chart-card {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: center;
}

.expense-donut {
  position: relative;
  width: min(52vw, 180px);
  aspect-ratio: 1;
  margin: 0 auto;
  border-radius: 50%;
}

.expense-donut::after {
  position: absolute;
  inset: 18%;
  content: "";
  border-radius: 50%;
  background: var(--color-surface);
}

.expense-donut-center {
  position: absolute;
  inset: 28%;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.expense-donut-center span {
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.expense-donut-center strong {
  color: var(--color-text);
  font-size: 0.95rem;
  overflow-wrap: anywhere;
}

.expense-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.expense-breakdown-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.breakdown-label-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 0.5rem;
  align-items: center;
  color: var(--color-text);
  font-size: 0.875rem;
}

.breakdown-color {
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 50%;
}

.breakdown-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.breakdown-label-row strong {
  color: var(--color-text);
  font-size: 0.875rem;
}

.breakdown-bar {
  height: 0.45rem;
  overflow: hidden;
  border-radius: var(--radius-full);
  background: var(--color-border-light);
}

.breakdown-bar-fill {
  height: 100%;
  min-width: 0.35rem;
  border-radius: inherit;
}

.no-chart-data {
  margin: 0;
  color: var(--color-text-muted);
  font-size: 0.875rem;
  text-align: center;
}

/* メンバーカード */
.summary-section {
  border-radius: var(--radius-lg);
  padding: 1.5rem;
}

.member-cards {
  display: grid;
  grid-template-columns: 1fr; /* mobile */
  gap: 0.75rem; /* mobile */
}

.member-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1rem; /* mobile */
  box-shadow: var(--shadow-sm);
  transition: transform var(--transition-base), box-shadow var(--transition-base);
}

.member-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.member-card.balance-positive {
  border-left: 4px solid var(--color-travel);
}

.member-card.balance-negative {
  border-left: 4px solid var(--color-danger);
}

.member-card.balance-zero {
  border-left: 4px solid var(--color-text-muted);
}

.member-header {
  display: flex;
  flex-direction: column; /* mobile: stack */
  align-items: flex-start; /* mobile */
  gap: 0.5rem; /* mobile */
  margin-bottom: 0.75rem; /* mobile */
  padding-bottom: 0.75rem; /* mobile */
  border-bottom: 1px solid var(--color-border);
}

.member-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--color-border);
}

.member-avatar .avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.member-avatar .avatar-placeholder {
  color: var(--color-text-muted);
}

.member-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-text);
}

.balance-badge {
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-full);
  font-weight: 600;
  font-size: 0.875rem;
}

.balance-badge.positive {
  background: var(--color-travel-light);
  color: var(--color-travel);
}

.balance-badge.negative {
  background: var(--color-danger-light);
  color: var(--color-danger);
}

.member-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.balance-status {
  align-self: flex-start;
  margin-bottom: 0.45rem;
  padding: 0.2rem 0.55rem;
  border-radius: var(--radius-full);
  background: var(--color-background);
  color: var(--color-text-muted);
  font-size: 0.75rem;
  font-weight: 700;
}

.balance-status.receive {
  background: var(--color-travel-light);
  color: var(--color-travel);
}

.balance-status.pay {
  background: var(--color-danger-light);
  color: var(--color-danger);
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
}

.detail-value.paid {
  color: var(--color-travel);
}

.detail-value.owed {
  color: var(--color-danger);
}

/* 精算リスト */
.settlements-section {
  border-radius: var(--radius-lg);
  padding: 1.5rem;
}

.settlement-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settlement-item {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1rem; /* mobile */
  display: flex;
  flex-direction: column; /* mobile */
  gap: 0.75rem; /* mobile */
  align-items: flex-start; /* mobile */
  box-shadow: var(--shadow-sm);
  transition: transform var(--transition-base), box-shadow var(--transition-base);
}

.settlement-item:hover {
  transform: translateX(4px);
  box-shadow: var(--shadow-md);
}

.settlement-item.settled {
  opacity: 0.72;
  background: var(--color-background);
}

.settlement-item.settled .from-member,
.settlement-item.settled .to-member,
.settlement-item.settled .arrow,
.settlement-item.settled .settlement-amount {
  text-decoration: line-through;
}

.settlement-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.125rem;
}

.from-member {
  font-weight: 600;
  color: var(--color-danger);
}

.arrow {
  color: var(--color-text-muted);
  font-size: 1.5rem;
}

.to-member {
  font-weight: 600;
  color: var(--color-travel);
}

.settlement-amount {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-text);
}

.status-badge {
  padding: 0.2rem 0.55rem;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 700;
}

.status-badge.unpaid {
  color: var(--color-review);
  background: var(--color-review-light);
}

.status-badge.paid {
  color: var(--color-travel);
  background: var(--color-travel-light);
}

.settlement-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.status-toggle {
  padding: 0.45rem 0.7rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.status-toggle-complete {
  border-color: var(--color-primary);
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
}

.status-toggle-undo {
  border-color: var(--color-review);
  background: var(--color-review-light);
  color: var(--color-text);
}

.status-toggle:disabled {
  opacity: 0.55;
  cursor: wait;
}

/* 精算不要 */
.no-settlements {
  border-radius: var(--radius-lg);
  padding: 3rem;
  text-align: center;
  color: var(--color-text-muted);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
}

.calculation-warning {
  margin-bottom: 1.5rem;
  padding: 1rem 1.25rem;
  border: 1px solid var(--color-review);
  border-left-width: 5px;
  border-radius: var(--radius-lg);
  background: var(--color-review-light);
  color: var(--color-text);
}

.calculation-warning h2 {
  margin: 0 0 0.35rem;
  font-size: 1rem;
  color: var(--color-text);
}

.calculation-warning p {
  margin: 0 0 0.5rem;
}

.calculation-warning ul {
  margin: 0;
  padding-left: 1.25rem;
}

.success-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: var(--color-primary-ink);
}

.no-settlements h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--color-text);
}

.no-settlements p {
  font-size: 1rem;
  color: var(--color-text-muted);
  opacity: 0.9;
}

/* 費用詳細 */
.expenses-section {
  border-radius: var(--radius-lg);
  padding: 1.5rem;
}

.expense-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.mobile-expense-filter-button,
.expense-filter-sidebar,
.expense-filter-overlay {
  display: none;
}

.filter-select {
  min-width: 10rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.filter-select:focus {
  outline: none;
  border-color: var(--color-primary-ink);
  box-shadow: 0 0 0 3px var(--color-primary-light);
}

.expense-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.expense-item {
  position: relative;
  z-index: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: all var(--transition-base);
}

.expense-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  background: var(--color-background);
}

.expense-header {
  display: flex;
  flex-direction: column; /* mobile */
  gap: 0.75rem; /* mobile */
  align-items: flex-start; /* mobile */
  margin-bottom: 0.75rem; /* mobile */
  padding-bottom: 0.75rem; /* mobile */
  border-bottom: 1px solid var(--color-border);
}

.expense-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.expense-icon {
  font-size: 1.5rem;
  color: var(--color-primary-ink);
}

.expense-cost {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--color-travel);
}

.expense-memo {
  margin: 0 0 0.75rem;
  color: var(--color-text-muted);
  font-size: 0.8125rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.expense-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-expense-members {
  display: none;
}

.expense-info {
  display: flex;
  flex-direction: column; /* mobile */
  gap: 0.25rem; /* mobile */
  font-size: 0.8125rem; /* mobile */
  color: var(--color-text-muted);
}

.memo-info span:last-child {
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.info-label {
  font-weight: 600;
  min-width: 80px;
  color: var(--color-text);
}

.payer-name {
  color: var(--color-travel);
  font-weight: 600;
}

.payer-chip {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  gap: 0.35rem;
  align-items: center;
  color: var(--color-travel);
  font-weight: 700;
}

.payer-icon {
  display: inline-flex;
  width: 1.35rem;
  height: 1.35rem;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-full);
  background: var(--color-travel-light);
  color: var(--color-travel);
}

.payer-chip-menu {
  justify-content: flex-end;
}

/* バッジ */
.expense-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-weight: 600;
  margin-left: 0.5rem;
}

.expense-badge.schedule {
  background: var(--color-travel-light);
  color: var(--color-travel);
}

.expense-badge.independent {
  background: var(--color-review-light);
  color: var(--color-review);
}

.burden-info {
  align-items: flex-start;
}

.burden-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.burden-chip {
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-full);
  background: var(--color-background);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  font-size: 0.75rem;
  font-weight: 600;
}

.expense-item.menu-open {
  z-index: 100;
}

.expense-badge.partial {
  background: var(--color-review-light);
  color: var(--color-review);
}

.expense-badge.settled {
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
}

.expense-badge.unsettled {
  background: var(--color-review-light);
  color: var(--color-review);
}

.expense-settlement-button {
  align-self: flex-end;
  padding: 0.4rem 0.75rem;
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all var(--transition-fast);
}

.expense-settlement-button.settlement-complete {
  border: 1px solid var(--color-primary);
  background: var(--color-primary-light);
  color: var(--color-primary-ink);
}

.expense-settlement-button.settlement-undo {
  border: 1px solid var(--color-review);
  background: var(--color-review-light);
  color: var(--color-text);
}

.expense-settlement-button:disabled {
  opacity: 0.55;
  cursor: wait;
}

/* 割り勘アイテム用スタイル */
.warikan-item {
  border-left: 4px solid var(--color-review);
}

.no-expenses {
  text-align: center;
  padding: 2rem;
  color: var(--color-text-muted);
  font-size: 0.875rem;
}

/* アクションボタン */
.expense-actions {
  position: relative;
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--color-border);
}

.mobile-expense-actions {
  display: none;
}

.expense-kebab-button {
  width: 32px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text-muted);
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
}

.expense-kebab-button:hover {
  background: var(--color-background);
  color: var(--color-text);
}

.expense-action-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 1000;
  min-width: 130px;
  overflow: hidden;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
}

.expense-action-item {
  width: 100%;
  padding: 0.625rem 0.75rem;
  border: none;
  background: var(--color-surface);
  color: var(--color-text);
  font-size: 0.8125rem;
  text-align: left;
  cursor: pointer;
  transition: background var(--transition-fast);
}

.expense-action-item:hover {
  background: var(--color-background);
}

.expense-action-delete {
  color: var(--color-danger);
}

.expense-menu-detail {
  display: grid;
  gap: 0.125rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--color-border-light);
}

.expense-menu-detail span {
  color: var(--color-text-muted);
  font-size: 0.625rem;
}

.expense-menu-detail strong {
  color: var(--color-text);
  font-size: 0.75rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}

/* フローティングボタン */
.floating-btn {
  position: fixed;
  bottom: 1rem; /* mobile */
  right: 1rem; /* mobile */
  width: 3.5rem; /* mobile */
  height: 3.5rem; /* mobile */
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--color-primary) 0%,
    var(--color-primary-hover) 100%
  );
  color: var(--color-primary-ink);
  border: none;
  box-shadow: var(--shadow-lg);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  z-index: 100;
}

.floating-btn:hover {
  transform: scale(1.05);
  box-shadow: var(--shadow-xl);
}

.floating-btn:active {
  transform: scale(0.98);
}

.floating-btn-icon {
  font-size: 1.25rem;
  line-height: 1;
  color: var(--color-primary-ink);
}

.floating-btn-icon img {
  filter: none;
}

/* ========================================
   レスポンシブ - モバイルファースト
   ======================================== */

/* タブレット (768px〜) */
@media (min-width: 768px) {
  .warikan-view {
    padding: 1.5rem 1rem;
  }

  .budget-section {
    grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  }

  .budget-summary-card,
  .expense-chart-card {
    padding: 1.25rem;
  }

  .budget-metrics {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .expense-chart-card {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .expense-donut {
    width: 150px;
  }

  .member-cards {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1rem;
  }

  .member-card {
    padding: 1.25rem;
  }

  .member-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }

  .settlement-item {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
  }

  .expense-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }

  .expense-info {
    flex-direction: row;
    gap: 0.5rem;
    font-size: 0.875rem;
  }
}

/* デスクトップ (1024px〜) */
@media (min-width: 1024px) {
  .warikan-view {
    padding: 2rem 1rem;
  }

  .member-cards {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }

  .member-card {
    padding: 1.5rem;
  }

  .floating-btn {
    bottom: 2rem;
    right: 2rem;
    width: 4rem;
    height: 4rem;
  }
}

@media (max-width: 640px) {
  .warikan-view {
    padding: 0;
  }

  .warikan-content {
    gap: 1rem;
  }

  .summary-section,
  .settlements-section,
  .expenses-section {
    padding: 0.75rem;
  }

  .budget-summary-card,
  .expense-chart-card {
    padding: 0.75rem;
    border-radius: var(--radius-lg);
  }

  .budget-metrics {
    gap: 0.5rem;
  }

  .budget-metric {
    padding: 0.6rem;
  }

  .budget-metric strong {
    font-size: 0.9375rem;
  }

  .expense-donut {
    width: min(48vw, 140px);
  }

  .expense-donut-center strong,
  .breakdown-label-row,
  .breakdown-label-row strong {
    font-size: 0.75rem;
  }

  .section-title {
    margin-bottom: 0.75rem;
    font-size: 1.125rem;
  }

  .member-card,
  .settlement-item,
  .expense-item {
    padding: 0.75rem;
    border-radius: var(--radius-lg);
  }

  .member-cards {
    display: flex;
    gap: 0.5rem;
    overflow-x: auto;
    padding-bottom: 0.25rem;
    scroll-snap-type: x proximity;
  }

  .member-card {
    min-width: min(78vw, 250px);
    scroll-snap-align: start;
  }

  .member-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .member-name,
  .expense-title {
    font-size: 0.9375rem;
  }

  .member-avatar {
    width: 32px;
    height: 32px;
  }

  .balance-badge,
  .detail-label,
  .expense-info {
    font-size: 0.75rem;
  }

  .detail-value {
    font-size: 0.875rem;
  }

  .settlement-content {
    width: 100%;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: 0.875rem;
  }

  .settlement-amount,
  .expense-cost {
    font-size: 1rem;
  }

  .settlement-controls {
    width: 100%;
    justify-content: space-between;
  }

  .status-toggle {
    padding: 0.35rem 0.5rem;
    font-size: 0.6875rem;
  }

  .expense-list,
  .settlement-list {
    gap: 0.5rem;
  }

  .desktop-expense-filters {
    display: none;
  }

  .mobile-expense-filter-button {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-text);
    font-size: 0.75rem;
    font-weight: 600;
  }

  .filter-active-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--color-primary);
  }

  .expense-filter-overlay {
    position: fixed;
    inset: 0;
    z-index: 200;
    display: block;
    background: rgba(18, 33, 52, 0.4);
  }

  .expense-filter-sidebar {
    position: fixed;
    top: 0;
    left: -100%;
    z-index: 201;
    display: flex;
    flex-direction: column;
    width: min(320px, 86vw);
    height: 100dvh;
    background: var(--color-surface);
    box-shadow: var(--shadow-xl);
    transition: left 0.25s ease;
  }

  .expense-filter-sidebar.is-open {
    left: 0;
  }

  .expense-filter-sidebar-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    background: var(--color-navy);
    color: var(--trip-header-fg);
  }

  .expense-filter-sidebar-header button {
    width: 32px;
    height: 32px;
    border: none;
    border-radius: var(--radius-md);
    background: rgba(255, 255, 255, 0.15);
    color: var(--trip-header-fg);
    font-size: 1.25rem;
    cursor: pointer;
  }

  .expense-filter-sidebar-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem;
  }

  .expense-filter-sidebar-content label {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    margin: 0;
    font-size: 0.75rem;
    color: var(--color-text);
  }

  .expense-filter-sidebar-content .filter-select {
    width: 100%;
    min-width: 0;
  }

  .expense-filter-reset {
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .expense-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: start;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
  }

  .expense-title {
    min-width: 0;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .expense-badge {
    margin-left: 0;
    padding: 0.125rem 0.375rem;
    font-size: 0.625rem;
  }

  .expense-details {
    display: none;
  }

  .mobile-expense-members {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.25rem;
    padding-right: 0.25rem;
  }

  .mobile-payer-summary,
  .mobile-participant-summary {
    display: inline-flex;
    min-width: 0;
    gap: 0.35rem;
    align-items: center;
  }

  .mobile-payer-summary {
    flex: 1 1 auto;
  }

  .mobile-participant-summary {
    flex: 0 0 auto;
  }

  .mobile-summary-label {
    color: var(--color-text-muted);
    font-size: 0.6875rem;
    font-weight: 700;
    white-space: nowrap;
  }

  .participant-avatars {
    display: inline-flex;
    align-items: center;
    padding-left: 0.25rem;
  }

  .participant-avatar {
    display: inline-flex;
    width: 1.7rem;
    height: 1.7rem;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 2px solid var(--color-surface);
    border-radius: var(--radius-full);
    background: var(--color-primary-light);
    color: var(--color-primary-ink);
    font-size: 0.6875rem;
    font-weight: 800;
    box-shadow: var(--shadow-sm);
  }

  .participant-avatar + .participant-avatar {
    margin-left: -0.45rem;
  }

  .participant-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .participant-more {
    background: var(--color-background);
    color: var(--color-text-muted);
  }

  .payer-chip {
    padding: 0.2rem 0.45rem 0.2rem 0.25rem;
    border-radius: var(--radius-full);
    background: var(--color-travel-light);
  }

  .mobile-expense-actions {
    display: flex;
  }

  .expense-actions {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }

  .expense-item {
    position: relative;
    padding-right: 3rem;
  }

  .expense-actions {
    position: absolute;
    top: 0.625rem;
    right: 0.625rem;
  }

  .expense-detail-menu {
    min-width: min(220px, 72vw);
  }
}
</style>
